import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from './MailRichEditor';
import 'quill/dist/quill.snow.css';
import axios from 'src/common/axiosInstance';
import HtmlMailViewer from './HtmlMailViewer';
import MailAudiencePicker, { contextVariables } from './MailAudiencePicker';
import './MailData.css';
import MailTemplatePicker from './MailTemplatePicker';
import { ArrowRight, Braces, FileText, Paperclip, Plus, X } from 'lucide-react';
import MailFieldMenu from './MailFieldMenu';
import CustomerDataPicker from '../data/CustomerDataPicker';
import useMailFieldHighlights from './useMailFieldHighlights';
import { fieldLabel, hasValue, slashQuery, matchingFields, resolveBindings } from './mailWriting';
import { starters, variableKeys } from './mailTemplates';
export default function MailComposer({
  connection,
  loadCustomerContext,
  customerRecordId,
  api = axios,
  initialContext = null,
  embedded = false,
  onSent,
  onBusy,
  initialTo = '',
  fromLabel = connection,
  onClose,
  onDirty,
  templateRequest = 0
}) {
  const [draft, setDraft] = useState({
    to: initialContext?.email || initialTo,
    cc: '',
    subject: '',
    body: '',
    format: 'html'
  });
  const [templates, setTemplates] = useState([]),
    [template, setTemplate] = useState(null),
    [name, setName] = useState('');
  const [variables, setVariables] = useState(initialContext ? contextVariables(initialContext) : {}),
    [files, setFiles] = useState([]),
    [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false),
    [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [context, setContext] = useState(initialContext), [audienceOpen, setAudienceOpen] = useState(false), [fieldSearch, setFieldSearch] = useState('');
  const editorRef = useRef(null), editorRange = useRef(null), subjectRef = useRef(null), insertTarget = useRef('body');
  const [bindings,setBindings]=useState({}), [slash,setSlash]=useState(null), [activeField,setActiveField]=useState(0), [mappingKey,setMappingKey]=useState('');
  const plainRef=useRef(null), writingCallbacks=useRef({});
  const resolved=useMemo(()=>resolveBindings(variables,bindings),[variables,bindings]);
  const keys=variableKeys(draft.subject,draft.body), missingKeys=keys.filter(key=>!hasValue(resolved[key]));
  const menuFields=matchingFields(variables,slash?.query||'').slice(0,40);
  const [picker, setPicker] = useState(false);
  const [dataPicker,setDataPicker]=useState(false);
  const sharedId=customerRecordId || context?.shared_contact_id || (context?.source==='contacts'?context.id:null);
  const sourceLoader=useMemo(()=>loadCustomerContext || (context?.organisation && sharedId ? (id,params,signal)=>api.get(`/customer-data/organisations/${encodeURIComponent(context.organisation)}/contacts/${encodeURIComponent(id)}`,{params,signal}).then(r=>r.data.data) : null),[loadCustomerContext,context?.organisation,sharedId,api]);
  const [showVariables, setShowVariables] = useState(true);
  const [showSave, setShowSave] = useState(false);
  const sendId = useRef(null);
  const composerRef = useRef(null);
  const highlightStyle=useMailFieldHighlights(composerRef,draft.body,resolved,!preview && draft.format==='html');
  useEffect(() => {
    const root = composerRef.current;
    const editor = root?.querySelector('.ql-editor');
    editor?.setAttribute('role', 'textbox');
    editor?.setAttribute('aria-label', 'Message');
    editor?.setAttribute('aria-multiline', 'true');
    for (const [selector, label] of [['.ql-bold', 'Bold'], ['.ql-italic', 'Italic'], ['.ql-underline', 'Underline'], ['.ql-list[value="ordered"]', 'Numbered list'], ['.ql-list[value="bullet"]', 'Bulleted list'], ['.ql-link', 'Insert link'], ['.ql-clean', 'Clear formatting']]) {
      root?.querySelector(selector)?.setAttribute('aria-label', label);
    }
  });
  const hasDraft = !!((embedded ? '' : draft.to) || draft.cc || draft.subject || draft.body.replace(/<[^>]*>/g, '').trim() || files.length);
  useEffect(() => { onDirty?.(hasDraft); }, [hasDraft, onDirty]);
  useEffect(() => { if (templateRequest) setPicker(true); }, [templateRequest]);
  const locked = busy || uncertain;
  useEffect(() => {
    onBusy?.(busy || uncertain);
  }, [busy, uncertain, onBusy]);
  const refresh = () => api.get('/mail/templates').then(({
    data
  }) => setTemplates(data.items || []));
  useEffect(() => {
    let active = true;
    api.get('/mail/templates').then(({
      data
    }) => {
      if (active) setTemplates(data.items || []);
    }).catch(() => {
      if (active) setError('Could not load saved templates.');
    });
    return () => { active = false; };
  }, [api]);
  const change = patch => {
    setDraft(d => ({
      ...d,
      ...patch
    }));
    setPreview(null);
    sendId.current = null;
  };
  const applyTemplate = id => {
    const t = [...starters, ...templates].find(t => t.id === id);
    if (!t) return;
    if ((files.length || draft.subject || draft.body.replace(/<[^>]*>/g, '').trim()) && !window.confirm('Replace the subject and body? If this template contains files, they will replace the current attachments too.')) return;
    setPicker(false);
    setTemplate(t.version ? t : null);
    setName(t.name);
    change({
      subject: t.subject,
      body: t.body,
      format: t.format
    });
    setVariables(context ? contextVariables(context) : {}); setBindings({});
    if(t.attachments?.length) setFiles(t.attachments.map(f=>({...f,size:Math.floor(f.data.length*3/4)})));
  };
  const usePerson = rows => {
    const row = rows[0];
    if (draft.to && draft.to !== row.email && !window.confirm('Replace the recipient and loaded customer values? The message text is kept for your review.')) return;
    setContext(row); setVariables(contextVariables(row)); setBindings({}); change({to: row.email}); setAudienceOpen(false);
    setNotice('Recipient and source values loaded. Review the correct opportunity and values before sending.');
  };
  const trackText = (input, target) => {
    insertTarget.current=target;
    const match=slashQuery(input.value,input.selectionStart ?? input.value.length);
    setSlash(match?{...match,target}:null);setActiveField(0);
  };
  const insertVariable = key => {
    const token = `{{${key}}}`, target=slash?.target || insertTarget.current;
    if(target==='subject' || draft.format==='text'){
      const input=target==='subject'?subjectRef.current:plainRef.current;
      const text=target==='subject'?draft.subject:draft.body;
      const start=slash?.start ?? input?.selectionStart ?? text.length, end=slash?.end ?? input?.selectionEnd ?? start;
      change({[target==='subject'?'subject':'body']:text.slice(0,start)+token+text.slice(end)});
      requestAnimationFrame(()=>{input?.focus();input?.setSelectionRange(start+token.length,start+token.length);});
    } else {
      const editor=editorRef.current?.getEditor();
      if(editor){
        const range=slash?{index:slash.start,length:slash.end-slash.start}:editor.getSelection()||editorRange.current||{index:Math.max(0,editor.getLength()-1),length:0};
        editor.history?.cutoff();
        editor.deleteText(range.index,range.length,'user');editor.insertText(range.index,token,'user');editor.setSelection(range.index+token.length,0);editor.focus();editor.history?.cutoff();
      }
    }
    setSlash(null);setActiveField(0);
  };
  const slashKeys = e => {
    if(!slash || e.isComposing)return;
    if(e.key==='Escape'){e.preventDefault();e.stopPropagation();setSlash(null);return;}
    if(['ArrowDown','ArrowUp'].includes(e.key)) {e.preventDefault();e.stopPropagation();setActiveField(n=>Math.max(0,Math.min(menuFields.length-1,n+(e.key==='ArrowDown'?1:-1))));}
    if(e.key==='Enter'&&menuFields[activeField]){e.preventDefault();e.stopPropagation();insertVariable(menuFields[activeField][0]);}
  };
  writingCallbacks.current={slashKeys,track:()=>{
    const q=editorRef.current?.getEditor(), range=q?.getSelection();
    if(!range || range.length)return;
    const match=slashQuery(q.getText(),range.index), bounds=match?q.getBounds(range.index):null;
    setSlash(match?{...match,target:'body',top:Math.min(bounds.top+70,280)}:null);setActiveField(0);
  }};
  useEffect(()=>{
    if(preview || draft.format!=='html')return;
    const q=editorRef.current?.getEditor();if(!q)return;
    let frame;
    const track=()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(()=>writingCallbacks.current.track());};
    const key=e=>writingCallbacks.current.slashKeys(e);
    q.on('text-change',track);q.on('selection-change',track);q.root.addEventListener('keydown',key,true);
    return()=>{cancelAnimationFrame(frame);q.off('text-change',track);q.off('selection-change',track);q.root.removeEventListener('keydown',key,true);};
  },[preview,draft.format]);
  useEffect(()=>{if(!slash)return;const node=composerRef.current?.querySelector('[role="option"][aria-selected="true"]');node?.scrollIntoView?.({block:'nearest'});},[activeField,slash]);
  useEffect(()=>{const outside=e=>{if(!e.target.closest?.('.mail-writing-field')&&!e.target.closest?.('.cdp-overlay'))setSlash(null);};document.addEventListener('pointerdown',outside);return()=>document.removeEventListener('pointerdown',outside);},[]);
  const menu = target => slash?.target===target && <MailFieldMenu onBrowse={sourceLoader?()=>setDataPicker(true):undefined} fields={menuFields} active={activeField} onChoose={insertVariable} style={target==='body'?{top:slash.top||44}:undefined}/>;
  const focusMapping=key=>{setShowVariables(true);setMappingKey(key);setTimeout(()=>composerRef.current?.querySelector('.mail-binding-search')?.focus(),0);};
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const payload = {
        name,
        subject: draft.subject,
        body: draft.body,
        format: draft.format,
        attachments: files.map(({name,data})=>({name,data})),
        ...(template ? {
          version: template.version
        } : {})
      };
      const {
        data
      } = template ? await api.put(`/mail/templates/${template.id}`, payload) : await api.post('/mail/templates', payload);
      setTemplate(data);
      await refresh();
      setNotice('Template saved privately. Attachments were saved. Recipients and customer variable values were not stored.');
    } catch (e) {
      setError(e.response?.data?.message || 'Template could not be saved.');
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!template || !window.confirm(`Delete template “${template.name}”?`)) return;
    setBusy(true);
    try {
      await api.delete(`/mail/templates/${template.id}`, {
        data: {
          version: template.version
        }
      });
      setTemplate(null);
      await refresh();
    } catch (e) {
      setError(e.response?.data?.message || 'Template could not be deleted.');
    } finally {
      setBusy(false);
    }
  };
  const attach = async event => {
    const selected = Array.from(event.target.files || []);
    event.target.value = '';
    setBusy(true);
    setPreview(null);
    setError('');
    try {
      if (files.length + selected.length > 3 || selected.some(f => f.size > 512000) || files.reduce((n, f) => n + f.size, 0) + selected.reduce((n, f) => n + f.size, 0) > 1024000) throw new Error('Choose up to 3 files, 500 KB each and 1 MB total.');
      const added = await Promise.all(selected.map(file => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('File could not be read.'));
        reader.onload = () => resolve({
          name: file.name.replace(/[^a-zA-Z0-9_. -]/g, '_'),
          data: String(reader.result).split(',')[1],
          size: file.size
        });
        reader.readAsDataURL(file);
      })));
      setFiles(f => [...f, ...added]);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const payload = () => ({
    ...draft,
    cc: draft.cc.split(',').map(v => v.trim()).filter(Boolean),
    variables: Object.fromEntries(variableKeys(draft.subject, draft.body).map(key => [key, resolved[key] ?? ''])),
    attachments: files.map(({
      name,
      data
    }) => ({
      name,
      data
    }))
  });
  const review = async event => {
    event.preventDefault();
    if (missingKeys.length) { focusMapping(missingKeys[0]); setError(`Choose a field or enter a value for: ${missingKeys.map(fieldLabel).join(', ')}.`); return; }
    setBusy(true);
    setError('');
    try {
      const {
        data
      } = await api.post('/mail/preview', payload());
      setPreview(data);
    } catch (e) {
      setError(Object.values(e.response?.data?.errors || {}).flat().join(' ') || e.response?.data?.message || 'Preview failed.');
    } finally {
      setBusy(false);
    }
  };
  const send = async () => {
    if (locked || !preview) return;
    setBusy(true);
    setError('');
    sendId.current ||= crypto.randomUUID();
    try {
      const {
        data
      } = await api.post('/mail/send', {
        ...payload(),
        connection,
        request_id: sendId.current
      });
      if (data.status !== 'sent') throw new Error('Unconfirmed');
      setNotice('Gmail accepted your email. Delivery and reading are not confirmed.');
      setDraft({
        to: embedded ? initialContext?.email || initialTo : '',
        cc: '',
        subject: '',
        body: '',
        format: 'html'
      });
      setFiles([]);
      setVariables({});
      setPreview(null);
      sendId.current = null;
      onSent();
    } catch (e) {
      const safe = e.response?.status === 422;
      setUncertain(!safe);
      setError(e.response?.data?.message || 'Send outcome is uncertain. Check Gmail Sent before sending again.');
      if (safe) sendId.current = null;
    } finally {
      setBusy(false);
    }
  };
  return <section ref={composerRef} className={`mail-compose ${showVariables && !preview ? 'mail-compose-with-context' : ''}`} aria-label="Compose email">
    <style>{highlightStyle}</style>
    <div className="mail-compose-document">
      <header className="mail-compose-heading"><div><h1>{preview ? 'Review your email' : 'New email'}</h1><p>{preview ? 'Check the recipients and content before sending.' : 'A personal note, ready for your customer.'}</p></div>{onClose && <button aria-label="Close composer" disabled={locked} onClick={onClose}><X size={19} /></button>}</header>
      {error && <p className="mail-alert" role="alert">{error}</p>}{notice && <p className="mail-notice" role="status">{notice}</p>}
      {!preview && <fieldset disabled={locked} className="mail-compose-fields"><form onSubmit={review}>
        <div className="mail-address-row"><span>From</span><strong>{fromLabel}</strong></div>
        <label className="mail-address-row">To<input required readOnly={embedded} type="email" placeholder="customer@example.com" maxLength={254} value={draft.to} onChange={e => { change({ to: e.target.value }); if(context && e.target.value !== context.email){setContext(null);setVariables({});setBindings({});} }} /></label>
        <label className="mail-address-row">CC<input placeholder="Add email addresses, separated by commas" maxLength={2600} value={draft.cc} onChange={e => change({ cc: e.target.value })} /></label>
        <div className="mail-writing-field"><label className="mail-address-row">Subject<input ref={subjectRef} onFocus={()=>{insertTarget.current='subject';}} onKeyDown={slashKeys} onSelect={e=>trackText(e.target,'subject')} required placeholder="Give your email a subject" maxLength={255} value={draft.subject} onChange={e => { change({ subject: e.target.value }); trackText(e.target,'subject'); }} /></label>{menu('subject')}</div>
        <div className="mail-editor-controls"><label>Format<select value={draft.format} onChange={e => { if (draft.body && !window.confirm('Changing format clears the message body. Continue?')) return; change({ format: e.target.value, body: '' }); }}><option value="html">Formatted email</option><option value="text">Plain text</option></select></label>{!embedded && <button type="button" onClick={() => setShowSave(v => !v)}><FileText size={16} />Save template</button>}</div>
        <div className="mail-writing-hint">Type <kbd>/</kbd> to find and insert a field. <span>Blue = ready · Amber = missing value</span></div><div className="mail-writing-field"><label className="sr-only" htmlFor="mail-body">Message</label>{draft.format === 'html' ? <ReactQuill ref={editorRef} onFocus={()=>{insertTarget.current='body';}} onChangeSelection={range=>{if(range)editorRange.current=range;}} formats={['header','bold','italic','underline','list','bullet','link']} readOnly={locked} theme="snow" value={draft.body} onChange={body => change({ body })} modules={{ toolbar: [[{ header: [2, 3, false] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link', 'clean']] }} /> : <textarea ref={plainRef} onKeyDown={slashKeys} onSelect={e=>trackText(e.target,'body')} id="mail-body" required maxLength={100000} value={draft.body} onFocus={()=>{insertTarget.current='body';}} onChange={e => { change({ body: e.target.value }); trackText(e.target,'body'); }} />}{menu('body')}</div>
        {keys.length>0 && <div className="mail-token-status" aria-label="Variable status"><span>{missingKeys.length?`${missingKeys.length} missing`:'All fields ready'}</span>{keys.map(key=><button type="button" key={key} className={hasValue(resolved[key])?'is-ready':'is-missing'} onClick={()=>focusMapping(key)} title={hasValue(resolved[key])?String(resolved[key]):'Choose a source field or enter a value'}>{fieldLabel(key)} · {hasValue(resolved[key])?'Ready':'Missing'}</button>)}</div>}
        <div className="mail-file-list">{files.map((f, i) => <span key={i}><FileText size={16} />{f.name} · {Math.ceil(f.size / 1024)} KB<button type="button" aria-label={`Remove ${f.name}`} onClick={() => { setFiles(files.filter((_, n) => n !== i)); setPreview(null); }}><X size={14} /></button></span>)}</div>
        <footer className="mail-compose-actions"><label className="mail-attach"><Paperclip size={18} />Attach<input aria-label="Attachments · PDF, PNG, JPEG or text" type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.txt" onChange={attach} /></label><button type="button" onClick={() => setPicker(true)}><FileText size={17} />Templates</button><button type="button" aria-expanded={showVariables} onClick={() => setShowVariables(v => !v)}><Braces size={17} />Variables</button><button className="mail-primary" type="submit">{busy ? 'Preparing…' : 'Review email'}<ArrowRight size={17} /></button></footer>
        <p className="mail-muted mail-file-hint">Up to 3 files · 500 KB per file · 1 MB total. This draft is kept only while this page stays open.</p>
      </form>
      {showSave && <section className="mail-save-template"><h2>Save as reusable template</h2><label>Template name<input maxLength={120} value={name} onChange={e => setName(e.target.value)} /></label><p className="mail-muted">Subject, body, format and current attachments are saved privately. Recipients and loaded customer values are never saved in the template.</p><button type="button" disabled={!name || !draft.subject || !draft.body} onClick={save}>{template ? 'Update template' : 'Save template'}</button>{template && <><button type="button" onClick={() => setTemplate(null)}>Save a new copy instead</button><button type="button" onClick={remove}>Delete template</button></>}</section>}
      <details className="mail-source"><summary>HTML source and variable syntax</summary>{draft.format === 'html' && <label>HTML source<textarea maxLength={100000} value={draft.body} onChange={e => change({ body: e.target.value })} /></label>}<p>Use {'{{customer.name}}'} or another named variable in your subject or body. Enter its authorized value in Customer details. No expressions or code run.</p></details>
      {hasDraft && <button type="button" className="mail-clear" onClick={() => { if (window.confirm('Clear this draft and its attachments?')) { change({ to: embedded ? initialContext?.email || initialTo : '', cc: '', subject: '', body: '', format: 'html' }); setFiles([]); setVariables(context ? contextVariables(context) : {}); setBindings({}); setTemplate(null); setName(''); } }}>Clear draft</button>}
      </fieldset>}
      {preview && <section className="mail-review"><h2>Ready to send?</h2><dl><dt>From</dt><dd>{fromLabel}</dd><dt>To</dt><dd>{draft.to}</dd>{draft.cc && <><dt>CC</dt><dd>{draft.cc}</dd></>}</dl><h3>{preview.subject}</h3>{preview.format === 'html' ? <HtmlMailViewer html={preview.body} title="Email preview" /> : <pre>{preview.body}</pre>}<p>{files.length} attachment(s){files.length > 0 && ` · ${files.map(f => f.name).join(', ')}`}</p><div className="mail-review-actions"><button disabled={locked} onClick={() => setPreview(null)}>Back to editing</button><button className="mail-primary" disabled={locked} onClick={send}>{busy ? 'Sending…' : 'Send email'}<ArrowRight size={17} /></button></div><p className="mail-muted">Gmail acceptance does not confirm delivery or reading.</p></section>}
      {uncertain && <p role="alert" className="mail-alert">This draft is locked. Check Gmail Sent before starting another email. <a href="https://mail.google.com/mail/u/0/#sent" target="_blank" rel="noreferrer">Open Gmail Sent</a></p>}
    </div>
    {showVariables && !preview && <aside className="mail-customer-panel" aria-label="Customer details"><h2><Braces size={21} />Customer data</h2><p>Personalize with the details you already know.</p>{sourceLoader && <button type="button" className="cdp-launch" disabled={locked} onClick={()=>setDataPicker(true)}><Plus size={16}/>Browse connected apps</button>}<fieldset disabled={locked}>
      {!embedded && <button type="button" onClick={()=>setAudienceOpen(true)}><Plus size={16}/>{context?'Change person or source':'Choose person & data'}</button>}
      {context && <div className="mail-context-person"><strong>{context.values?.name || context.name || context.email}</strong><span>{context.email}</span><small>{context.source === 'crm' ? 'CRM opportunity' : context.source} · Loaded for this email</small>{context.values?.stage && <small>{context.values.stage}{context.values.pipeline ? ` · ${context.values.pipeline}` : ''}</small>}</div>}
      <div className="mail-data-tabs"><button type="button" aria-pressed={!mappingKey} onClick={()=>setMappingKey('')}>Fields</button><button type="button" aria-pressed={!!mappingKey} disabled={!keys.length} onClick={()=>setMappingKey(missingKeys[0]||keys[0])}>In this email {keys.length}{missingKeys.length?` · ${missingKeys.length} missing`:''}</button></div>
      {mappingKey ? <section className="mail-binding-editor">
        <label>Variable<select aria-label="Variable to map" value={mappingKey} onChange={e=>setMappingKey(e.target.value)}>{[...new Set([...keys,mappingKey])].map(key=><option key={key} value={key}>{fieldLabel(key)}{hasValue(resolved[key])?'':' — Missing'}</option>)}</select></label>
        <small>{`{{${mappingKey}}}`}</small>
        <p>Choose a loaded field, or enter a value for this email.</p>
        <input className="mail-binding-search" aria-label="Find a source field" placeholder="Search name, company, stage…" value={fieldSearch} onChange={e=>setFieldSearch(e.target.value)}/>
        <div className="mail-binding-options">{matchingFields(variables,fieldSearch).map(([key,value])=><button type="button" key={key} aria-pressed={bindings[mappingKey]===key} onClick={()=>{setBindings(b=>({...b,[mappingKey]:key}));setPreview(null);setError('');}}><span>{fieldLabel(key)}<small>{hasValue(value)?String(value):'Not provided'}</small></span></button>)}</div>
        <label>Value for {fieldLabel(mappingKey)}<input aria-label={`Value for ${mappingKey}`} value={resolved[mappingKey]??''} placeholder="Enter a value for this email" maxLength={4000} onChange={e=>{setBindings(b=>{const next={...b};delete next[mappingKey];return next;});setVariables(v=>({...v,[mappingKey]:e.target.value}));setPreview(null);setError('');}}/></label>
        <p className={hasValue(resolved[mappingKey])?'mail-value-ready':'mail-value-missing'}>{hasValue(resolved[mappingKey])?'Ready to use': 'Missing — select a field or enter a value'}</p>
      </section> : <><label className="mail-field-search">Available fields<input aria-label="Search available fields" placeholder="Find a field…" value={fieldSearch} onChange={e=>setFieldSearch(e.target.value)}/></label><div className="mail-context-fields">{matchingFields(variables,fieldSearch).map(([key,value])=><div className="mail-variable" key={key}><span><strong>{fieldLabel(key)}</strong><small>{hasValue(value)?String(value):'Not provided'}</small></span><button type="button" aria-label={`Insert ${key}`} onMouseDown={e=>e.preventDefault()} onClick={()=>insertVariable(key)}><Plus size={16}/></button></div>)}</div></>}
      <p className="mail-muted">Data stays in its source app. Inserting a field does not edit the customer. Review values before sending.</p></fieldset></aside>}
    {dataPicker && sourceLoader && !locked && <CustomerDataPicker recordId={sharedId} loadContext={sourceLoader} onClose={()=>{setDataPicker(false);setSlash(null);}} onInsert={field=>{setVariables(v=>({...v,[field.key]:field.value}));if(mappingKey){setBindings(b=>({...b,[mappingKey]:field.key}));setPreview(null);}else insertVariable(field.key);setDataPicker(false);}}/>}
    {audienceOpen && !locked && <MailAudiencePicker api={api} initialOrganisation={context?.organisation || ''} onChoose={usePerson} onClose={()=>setAudienceOpen(false)}/>}
    {picker && !locked && <MailTemplatePicker templates={templates} onChoose={applyTemplate} onClose={() => setPicker(false)} />}
  </section>;
}
